import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { User } from "src/app/users/models/user";
import { PaymentFilter, TipoPayment } from "../models/payment-filter";
import { PaymentResult } from "../models/payment-result";
import { ChargesService } from "./charges.service";
import { TabFilter } from "../charges.component";
import { SingleCharge, SingleChargeResult } from "../models/single-charge";
import { Title, TitlesResponse } from "../models/titulos";
import { Payment } from "../models/payment";
import { environment } from "src/environments/environment";
@Injectable({
  providedIn: "root",
})
export class ChargesStoreService {
  private emptyPaymentResult: PaymentResult = {
    result: [],
    total_docs: 0,
    page: 1,
    limit: 10,
    total: { value: 0, parcels: 0 },
    credit_card: { value: 0, parcels: 0 },
    debit_card: { value: 0, parcels: 0 },
    money: { value: 0, parcels: 0 },
  };

  private emptyTitleOfSingleCharges: TitlesResponse = {
    result: [],
    page: 1,
    totalDocs: 0,
    totalPages: 1,
    limit: 10,
  };

  private _tabFilter: TabFilter = "aguardando";

  private _paymentFilter: PaymentFilter = {
    dt_recebimento: { fromDate: null, toDate: null },
    dt_agenda: { fromDate: null, toDate: null },
    contrato: [],
    cobrador: "",
    tipoPayment: "Recebimento",
  } as PaymentFilter;

  private _collectors: User[] = [];
  private _operators: User[] = [];

  private readonly _waitApprovalsPayments = new BehaviorSubject<PaymentResult>(this.emptyPaymentResult);
  private readonly _approvedPayments = new BehaviorSubject<PaymentResult>(this.emptyPaymentResult);
  private readonly _recurrencesPayments = new BehaviorSubject<PaymentResult>(this.emptyPaymentResult);
  private readonly _synchronizedPayments = new BehaviorSubject<PaymentResult>(this.emptyPaymentResult);
  private readonly _rescheduledPayments = new BehaviorSubject<PaymentResult>(this.emptyPaymentResult);
  private readonly _errorsPayments = new BehaviorSubject<PaymentResult>(this.emptyPaymentResult);
  private readonly _rejectedPayments = new BehaviorSubject<PaymentResult>(this.emptyPaymentResult);
  private readonly _returnPayments = new BehaviorSubject<PaymentResult>(this.emptyPaymentResult);
  private readonly _estornadosPayments = new BehaviorSubject<PaymentResult>(this.emptyPaymentResult);
  private readonly _singleCharges = new BehaviorSubject<SingleChargeResult>({
    result: [],
    total_docs: 0,
    page: 1,
    limit: 10,
  });
  private readonly _titlesSingleCharges = new BehaviorSubject<TitlesResponse>(this.emptyTitleOfSingleCharges);
  private readonly _notifiedPayments = new BehaviorSubject<PaymentResult>(this.emptyPaymentResult);

  readonly waitApprovalsPayments$ = this._waitApprovalsPayments.asObservable();
  readonly approvedPayments$ = this._approvedPayments.asObservable();
  readonly synchronizedPayments$ = this._synchronizedPayments.asObservable();
  readonly rescheduledPayments$ = this._rescheduledPayments.asObservable();
  readonly recurrencesPayments$ = this._recurrencesPayments.asObservable();
  readonly errorsPayments$ = this._errorsPayments.asObservable();
  readonly rejectedPayments$ = this._rejectedPayments.asObservable();
  readonly returnPayments$ = this._returnPayments.asObservable();
  readonly estornadosPayments$ = this._estornadosPayments.asObservable();
  readonly singleCharges$ = this._singleCharges.asObservable();
  readonly titlesSingleCharges$ = this._titlesSingleCharges.asObservable();
  readonly notifiedPayments$ = this._notifiedPayments.asObservable();

  get tabFilter() {
    return this._tabFilter;
  }
  set tabFilter(val: TabFilter) {
    this._tabFilter = val;
  }

  get paymentFilter() {
    return this._paymentFilter;
  }
  set paymentFilter(val: PaymentFilter) {
    this._paymentFilter = val;
  }

  get collectors() {
    return this._collectors;
  }
  set collectors(val: User[]) {
    this._collectors = val;
  }

  get operators() {
    return this._operators;
  }
  set operators(val: User[]) {
    this._operators = val;
  }

  private get waitApprovalsPayments() {
    return this._waitApprovalsPayments.getValue();
  }
  private set waitApprovalsPayments(val: PaymentResult) {
    this._waitApprovalsPayments.next(val);
  }

  private get approvedPayments() {
    return this._approvedPayments.getValue();
  }
  private set approvedPayments(val: PaymentResult) {
    this._approvedPayments.next(val);
  }

  private get recurrencesPayments() {
    return this._recurrencesPayments.getValue();
  }
  private set recurrencesPayments(val: PaymentResult) {
    this._recurrencesPayments.next(val);
  }

  private get synchronizedPayments() {
    return this._synchronizedPayments.getValue();
  }
  private set synchronizedPayments(val: PaymentResult) {
    this._synchronizedPayments.next(val);
  }

  private get rescheduledPayments() {
    return this._rescheduledPayments.getValue();
  }
  private set rescheduledPayments(val: PaymentResult) {
    this._rescheduledPayments.next(val);
  }

  private get errorsPayments() {
    return this._errorsPayments.getValue();
  }
  private set errorsPayments(val: PaymentResult) {
    this._errorsPayments.next(val);
  }

  private get rejectedPayments() {
    return this._rejectedPayments.getValue();
  }
  private set rejectedPayments(val: PaymentResult) {
    this._rejectedPayments.next(val);
  }

  private get returnPayments() {
    return this._returnPayments.getValue();
  }
  private set returnPayments(val: PaymentResult) {
    this._returnPayments.next(val);
  }

  private get estornadosPayments() {
    return this._estornadosPayments.getValue();
  }
  private set estornadosPayments(val: PaymentResult) {
    this._estornadosPayments.next(val);
  }

  private get singleCharges() {
    return this._singleCharges.getValue();
  }
  private set singleCharges(val: SingleChargeResult) {
    this._singleCharges.next(val);
  }

  private get titlesSingleCharges() {
    return this._titlesSingleCharges.getValue();
  }
  private set titlesSingleCharges(val: TitlesResponse) {
    this._titlesSingleCharges.next(val);
  }

  private get notifiedPayments() {
    return this._notifiedPayments.getValue();
  }
  private set notifiedPayments(val: PaymentResult) {
    this._notifiedPayments.next(val);
  }

  constructor(private chargesService: ChargesService) {
    this.addWaitApprovalsPayments(this.emptyPaymentResult);
    this.addApprovedPayments(this.emptyPaymentResult);
    this.addSynchronizedPayments(this.emptyPaymentResult);
    this.addRecurrencesPayments(this.emptyPaymentResult);
    this.addRescheduledPayments(this.emptyPaymentResult);
    this.addErrorsPayments(this.emptyPaymentResult);
    this.addRejectedPayments(this.emptyPaymentResult);
    this.addReturnPayments(this.emptyPaymentResult);
    this.addEstornadosPayments(this.emptyPaymentResult);
    this.addNotifiedPayments(this.emptyPaymentResult);
    this.addSingleCharge(this._singleCharges.getValue());
    this.addTitleSingleCharge(this.emptyTitleOfSingleCharges);
  }

  addWaitApprovalsPayments(payments: PaymentResult) {
    this.waitApprovalsPayments = payments;
  }
  addApprovedPayments(payments: PaymentResult) {
    this.approvedPayments = payments;
  }
  addRecurrencesPayments(payments: PaymentResult) {
    this.recurrencesPayments = payments;
  }
  addSynchronizedPayments(payments: PaymentResult) {
    this.synchronizedPayments = payments;
  }
  addRescheduledPayments(payments: PaymentResult) {
    this.rescheduledPayments = payments;
  }
  addErrorsPayments(payments: PaymentResult) {
    this.errorsPayments = payments;
  }
  addRejectedPayments(payments: PaymentResult) {
    this.rejectedPayments = payments;
  }
  addReturnPayments(payments: PaymentResult) {
    this.returnPayments = payments;
  }
  addEstornadosPayments(payments: PaymentResult) {
    this.estornadosPayments = payments;
  }
  addSingleCharge(singleCharge: SingleChargeResult) {
    this.singleCharges = singleCharge;
  }
  addTitleSingleCharge(titlesSingleCharge: TitlesResponse) {
    this.titlesSingleCharges = titlesSingleCharge;
  }
  addNotifiedPayments(notifiedPayments: PaymentResult) {
    this.notifiedPayments = notifiedPayments;
  }

  async getPayments(cnpj: string, limit: number, page: number) {
    const payments = await this.chargesService
      .getPayments(cnpj, this.tabFilter, this.paymentFilter, limit, page)
      .toPromise();

    payments.page = page;
    payments.limit = limit;

    // Aplicar filtro por tipo de pagamento
    let filteredPayments = this.filterPaymentsByType(payments);

    // Para aba notificado, buscar também registros de outras abas que tenham notificado: true
    if (this.tabFilter.includes("notificado")) {
      filteredPayments = await this.getNotifiedPaymentsFromAllTabs(cnpj, filteredPayments, limit, page);
    }

    if (this.tabFilter.includes("aguardando")) this.addWaitApprovalsPayments(filteredPayments);
    if (this.tabFilter.includes("aprovado")) this.addApprovedPayments(filteredPayments);
    if (this.tabFilter.includes("sincronizado")) this.addSynchronizedPayments(filteredPayments);
    if (this.tabFilter.includes("remarcado")) this.addRescheduledPayments(filteredPayments);
    if (this.tabFilter.includes("recorrencia")) this.addRecurrencesPayments(filteredPayments);
    if (this.tabFilter.includes("erro")) this.addErrorsPayments(filteredPayments);
    if (this.tabFilter.includes("rejeitado")) this.addRejectedPayments(filteredPayments);
    if (this.tabFilter.includes("devolvido")) this.addReturnPayments(filteredPayments);
    if (this.tabFilter.includes("estornado")) this.addEstornadosPayments(filteredPayments);
    if (this.tabFilter.includes("notificado")) this.addNotifiedPayments(filteredPayments);
  }

  private async getNotifiedPaymentsFromAllTabs(
    cnpj: string,
    currentPayments: PaymentResult,
    limit: number,
    page: number
  ): Promise<PaymentResult> {
    try {
      // Buscar em todas as outras abas por registros notificados
      const statusToSearch = ["sincronizado", "aprovado", "aguardando", "erro", "rejeitado"];
      let allNotifiedPayments = [...currentPayments.result];

      for (const status of statusToSearch) {
        const paymentsFromStatus = await this.chargesService
          .getPayments(cnpj, status, this.paymentFilter, 100, 1) // Buscar mais registros para encontrar notificados
          .toPromise();

        // Filtrar apenas os que são notificados
        const notifiedFromThisStatus = paymentsFromStatus.result.filter(payment => payment.notificado === true);

        // Adicionar evitando duplicatas
        notifiedFromThisStatus.forEach(payment => {
          if (!allNotifiedPayments.find(existing => existing._id === payment._id)) {
            allNotifiedPayments.push(payment);
          }
        });
      }

      return {
        ...currentPayments,
        result: allNotifiedPayments,
        total_docs: allNotifiedPayments.length,
      };
    } catch (error) {
      console.error("Erro ao buscar notificações em outras abas:", error);
      return currentPayments;
    }
  }

  async getSingleCharges(cnpj: string, limit: number, page: number) {
    await this.chargesService
      .getSingleCharges(cnpj, limit, page, this.paymentFilter)
      .toPromise()
      .then(payments => {
        payments.page = page;
        payments.limit = limit;
        this.addSingleCharge(payments);
      });
  }

  async getTitleSingleCharges(cnpj: string, limit: number, page: number, contrato: string, cgc_cliente: string) {
    await this.chargesService
      .getTitlesCharges(cnpj, limit, page, contrato, cgc_cliente)
      .toPromise()
      .then(titlesSingleCharges => {
        titlesSingleCharges.page = page;
        titlesSingleCharges.limit = limit;
        this.addTitleSingleCharge(titlesSingleCharges);
      });
  }

  async saveSingleChargeLink(cobrador: string, cnpj: string, titles: Title[], singleCharge: SingleCharge) {
    try {
      await this.chargesService.saveSingleChargeLink(cobrador, singleCharge, titles, cnpj);
    } catch (e) {
      console.log(e);
    }
  }
  getPaymentType(type: string): string {
    switch (type) {
      case "DI":
        return "Dinheiro";
      case "CC":
        return "Cartão de Crédito";
      case "CD":
        return "Cartão de Débito";
      case "PX":
        return "Pix";
      case "MULTFORMAS":
        return "Múltiplos pagamentos";
      default:
        return "";
    }
  }

  getPaymentTypesString(payment: Payment): string {
    const formas = new Set<string>();

    if (payment.baixas && payment.baixas.length > 0) {
      payment.baixas.forEach(baixa => {
        if (baixa.forma_pagamento) {
          const formaPagamento = this.getPaymentType(baixa.forma_pagamento);
          if (formaPagamento) {
            formas.add(formaPagamento);
          }
        }
      });
    }

    if (formas.size === 0 && payment.forma_pagamento) {
      const formaPagamento = this.getPaymentType(payment.forma_pagamento);
      if (formaPagamento) {
        formas.add(formaPagamento);
      }
    }

    if (formas.size === 0) {
      return "Não especificado";
    }

    return Array.from(formas).join(", ");
  }
  getCollectorName(code: string): string {
    const collectorFound = this._collectors.find(c => c.codigo_externo === code);
    console.log(this._collectors);
    console.log(collectorFound);
    return collectorFound ? collectorFound.nome : code;
  }

  getOperatorName(code: string): string {
    const operatorFound = this._operators.find(o => o.codigo_externo === code);
    return operatorFound ? operatorFound.nome : code;
  }

  getFilialName(cnpj: string): string {
    // Se não há dados do usuário ou empresas, retorna string vazia
    if (!environment.user || !environment.user.empresas || !cnpj) {
      return "";
    }

    // Normalizar CNPJ removendo pontos, barras e traços
    const normalizedCnpj = cnpj.replace(/[./-]/g, "");
    const filial = environment.user.empresas.find((empresa: any) => {
      if (!empresa.cnpj) return false;
      const empresaCnpj = empresa.cnpj.replace(/[./-]/g, "");
      return empresaCnpj === normalizedCnpj;
    });
    return filial ? filial.fantasia : "";
  }

  getPixStatus(
    cnpj: string,
    cgcCliente: string = "",
    txid: string = "",
    status: string = "REGISTRADO",
    dtInicial?: string,
    dtFinal?: string,
    turno?: string,
    filial?: boolean,
    codigoExterno?: string
  ) {
    return this.chargesService.getPixStatus(
      cnpj,
      cgcCliente,
      txid,
      status,
      dtInicial,
      dtFinal,
      turno,
      filial,
      codigoExterno
    );
  }

  // Método para obter o tipo de um pagamento baseado na lógica do getTipoPayment
  getTipoPayment(payment: Payment): TipoPayment {
    const foiReagendado = payment.add_reagendado || (payment.dt_reagendado && payment.dt_reagendado.length > 10);

    if (payment.notificado || payment.status === "notificado") {
      return "Notificado";
    } else if (payment.cod_motivo_devolucao && payment.cod_motivo_devolucao.length > 2) {
      return "Ocorrência";
    } else if (payment.mot_reprovado && payment.mot_reprovado.length > 2) {
      return "Reprovada";
    } else if (foiReagendado) {
      return "Reagendada";
    } else {
      return "Recebimento";
    }
  }

  // Método para filtrar payments por tipo
  private filterPaymentsByType(paymentResult: PaymentResult): PaymentResult {
    if (!this._paymentFilter.tipoPayment || this._paymentFilter.tipoPayment === "Todos") {
      return paymentResult;
    }

    const filteredResult = paymentResult.result.filter(
      payment => this.getTipoPayment(payment) === this._paymentFilter.tipoPayment
    );

    const totalDocs =
      paymentResult.total_docs !== undefined && paymentResult.total_docs !== null
        ? paymentResult.total_docs
        : filteredResult.length;

    return {
      ...paymentResult,
      result: filteredResult,
      total_docs: totalDocs,
    };
  }

  // Opções de tipos de pagamento para o filtro
  getTiposPaymentOptions(): TipoPayment[] {
    return ["Todos", "Notificado", "Ocorrência", "Reprovada", "Reagendada", "Recebimento"];
  }
}
